// Third-party
import React from 'react';
import round from 'lodash/round';

// Components
import GroupContentStep from './steps/GroupContentStep';
import GroupInfoStep from './steps/GroupInfoStep';
import GroupValuesStep from './steps/GroupValuesStep';
import { Modal, ModalBody, ModalFooter, ModalHeader, ModalTitle } from '@capintel/component-lib/modal';
import { Tabs } from '@capintel/component-lib/tabs';
import { Button } from '@capintel/component-lib/button';
import { Inline } from '@capintel/component-lib/inline';
import { Stack } from '@capintel/component-lib/stack';

// Utils
import { COMPARISON_ITEM_TYPES } from '../../../../utils/constants';
import { generateNewItemColor, VALID_GROUP_PRODUCTS } from '../../../../utils/comparison';
import { formatLocalizedString, formatMessage } from '../../../../utils/i18n';
import ComparisonItem from '@/types/comparison-item';
import ComparisonGroupProduct from '@/types/comparison-group-product';

type Props = {
  className?: string;
  show: boolean;
  comparisonItems: ComparisonItem[];
  onCancel: () => void;
  onConfirm: (name: string, color: string, products: ComparisonGroupProduct[]) => void;
  groupToEdit?: ComparisonItem;
};

export enum ComparisonModalGroupsTabOption {
  Items = 'ITEMS',
  ValuesFees = 'VALUES_AND_FEES',
}

enum AddGroupModalStep {
  GroupInfo,
  GroupContent,
  GroupValues,
}

type PartialComparisonGroupProduct = {
  itemId: string;
  dollars?: number;
  fee?: number;
};

type State = {
  name: string;
  step: AddGroupModalStep;
  groupColor: string;
  groupProducts: PartialComparisonGroupProduct[];
};

class AddComparisonGroupModal extends React.Component<Props, State> {
  mapInitialProductFees = (products: ComparisonGroupProduct[]): PartialComparisonGroupProduct[] =>
    products.map(product => ({
      ...product,
      fee: product.fee != null ? round(product.fee * 100, 6) : undefined,
    }));

  private initialState: State = {
    groupProducts: this.mapInitialProductFees(this.props.groupToEdit?.groupItems || []),
    groupColor: this.props.groupToEdit?.color || generateNewItemColor(this.props.comparisonItems),
    name: this.props.groupToEdit ? formatLocalizedString(this.props.groupToEdit.name) : '',
    step: this.props.groupToEdit ? AddGroupModalStep.GroupContent : AddGroupModalStep.GroupInfo,
  };

  readonly state: State = this.initialState;

  onNameChange = (name: string) => {
    this.setState({ name });
  };

  onFeeChange = (value: string, id: string) => {
    const { groupProducts } = this.state;
    const newState = groupProducts.map(item => {
      if (item.itemId === id) {
        let trueValue: number | undefined = +value;
        if (value === '') {
          trueValue = undefined;
        }
        return { ...item, fee: trueValue };
      }

      return item;
    });

    this.setState({ groupProducts: newState });
  };

  onValueChange = (value: string, id: string) => {
    const { groupProducts } = this.state;

    const inputValue: number | undefined = +value;

    const newState = groupProducts.map(item => {
      if (item.itemId === id) {
        let trueValue: number | undefined = +inputValue.toFixed(2);
        if (value === '') {
          trueValue = undefined;
        }
        return { ...item, dollars: trueValue };
      }

      return item;
    });

    this.setState({ groupProducts: newState });
  };

  onNext = () => {
    const nextStep = this.getNextStep();

    if (nextStep) {
      this.setState({ step: nextStep });
    }
  };

  getNextStep = (): AddGroupModalStep | null => {
    switch (this.state.step) {
      case AddGroupModalStep.GroupInfo:
        return AddGroupModalStep.GroupContent;
      case AddGroupModalStep.GroupContent:
        return AddGroupModalStep.GroupValues;
      default:
        return null;
    }
  };

  onPrevious = () => {
    const lastStep = this.getPreviousStep();

    if (lastStep) {
      this.setState({ step: lastStep });
    }
  };

  getPreviousStep = (): AddGroupModalStep | null => {
    switch (this.state.step) {
      case AddGroupModalStep.GroupContent:
        return AddGroupModalStep.GroupInfo;
      case AddGroupModalStep.GroupValues:
        return AddGroupModalStep.GroupContent;
      default:
        return null;
    }
  };

  onCancel = () => {
    this.props.onCancel();
    this.setState(this.initialState);
  };

  onRemoveItem = (item: ComparisonItem) => {
    this.setState(prevState => ({
      groupProducts: prevState.groupProducts.filter(product => product.itemId !== item._id),
    }));
  };

  onAddItem = (item: ComparisonItem) => {
    this.setState(prevState => ({
      groupProducts: prevState.groupProducts.concat({
        itemId: item._id,
        dollars: item.size || undefined,
        fee: item.discretionaryMer !== undefined ? item.discretionaryMer * 100 : undefined,
      }),
    }));
  };

  addGroupIsDisabled = () => {
    const { groupProducts, name } = this.state;
    const invalidName = name === null || name === '' || name.trim().length === 0;
    const noProducts = groupProducts.length === 0;
    const someProductHasNoValue = groupProducts.some(product => product.dollars === undefined || product.dollars <= 0);
    return invalidName || noProducts || someProductHasNoValue;
  };

  onAddGroup = () => {
    const { name, groupProducts, groupColor } = this.state;
    if (groupProducts.some(product => product.dollars === undefined)) {
      // Safeguard - should not happen as the modal should force you to enter the dollars to enable the button
      return;
    }

    const completeGroups = groupProducts as ComparisonGroupProduct[];
    this.props.onConfirm(
      name,
      groupColor,
      completeGroups.map(product => ({
        ...product,
        fee: product.fee ? round(product.fee / 100, 6) : undefined,
      })),
    );
  };

  nextIsDisabled = () => {
    const { name, groupProducts } = this.state;
    const invalidName = name === null || name === '' || name.trim().length === 0;

    switch (this.state.step) {
      case AddGroupModalStep.GroupInfo:
        return invalidName;
      case AddGroupModalStep.GroupContent:
        return groupProducts.length <= 0 || invalidName;
      case AddGroupModalStep.GroupValues:
        return invalidName; // TODO: Logic to not allow if values and fees arent entered
      default:
        return false;
    }
  };

  getAllItems = () => this.props.comparisonItems.filter(item => VALID_GROUP_PRODUCTS.includes(item.type as COMPARISON_ITEM_TYPES));

  getSelectedItems = () => {
    const { groupProducts } = this.state;
    return this.getAllItems().filter(item => groupProducts.some(groupProduct => groupProduct.itemId === item._id));
  };

  getUnselectedItems = () => {
    const { groupProducts } = this.state;
    return this.getAllItems().filter(item => !groupProducts.some(groupProduct => groupProduct.itemId === item._id));
  };

  renderModalContent = () => {
    const { name, step, groupProducts, groupColor } = this.state;

    const selectedItems = this.getSelectedItems();
    const unselectedItems = this.getUnselectedItems();

    switch (step) {
      case AddGroupModalStep.GroupInfo: {
        return <GroupInfoStep editable={false} name={name} onNameChange={this.onNameChange} />;
      }
      case AddGroupModalStep.GroupContent: {
        return (
          <GroupContentStep
            name={name}
            groupColor={groupColor}
            onNameChange={this.onNameChange}
            unselectedComparisonItems={unselectedItems}
            selectedComparisonItems={selectedItems}
            onAddItem={this.onAddItem}
            onRemoveItem={this.onRemoveItem}
            isEditing={!!this.props.groupToEdit}
          />
        );
      }
      case AddGroupModalStep.GroupValues: {
        return (
          <GroupValuesStep
            name={name}
            groupColor={groupColor}
            onNameChange={this.onNameChange}
            selectedItems={selectedItems}
            selectedItemDetails={groupProducts}
            onFeeChange={this.onFeeChange}
            onValueChange={this.onValueChange}
            isEditing={!!this.props.groupToEdit}
          />
        );
      }
    }
  };

  getTabsOptions = () => [
    {
      value: ComparisonModalGroupsTabOption.Items,
      i18nKey: 'Comparison.Groups.Tabs.Items',
    },
    {
      value: ComparisonModalGroupsTabOption.ValuesFees,
      i18nKey: 'Comparison.Groups.Tabs.ValuesFees',
    },
  ];

  render() {
    const { show, groupToEdit } = this.props;
    const { step, name, groupColor, groupProducts } = this.state;

    const searchOptions = this.getTabsOptions();

    const nextIsDisabled = this.nextIsDisabled();

    // Button display logic
    const showPreviousButton = step === AddGroupModalStep.GroupValues;
    const showAddGroupButton = step === AddGroupModalStep.GroupValues;
    const showNextButton = step !== AddGroupModalStep.GroupValues;

    return (
      <Modal open={show} onOpenChange={open => !open && this.onCancel()}>
        <div className="add-comparison-group-modal">
          {groupToEdit && <span className="edit-group-color-border" style={{ backgroundColor: groupColor }} />}
          <ModalHeader showCloseButton>
            <ModalTitle>
              {!groupToEdit ? (
                formatMessage('AddComparisonGroupModal.Modal.Title')
              ) : (
                <Inline gap="200">
                  <div className="group-color" style={{ backgroundColor: groupColor }} />
                  <GroupInfoStep compact name={name} onNameChange={this.onNameChange} editable />
                </Inline>
              )}
            </ModalTitle>
          </ModalHeader>
          <form onSubmit={e => e.preventDefault()}>
            <ModalBody>
              {groupToEdit ? (
                <Tabs defaultValue={ComparisonModalGroupsTabOption.Items}>
                  <Stack gap="200">
                    <Tabs.TabList>
                      {searchOptions.map(option => (
                        <Tabs.Tab key={option.value} value={option.value}>
                          {formatMessage(option.i18nKey)}
                        </Tabs.Tab>
                      ))}
                    </Tabs.TabList>
                    <Tabs.Content value={ComparisonModalGroupsTabOption.Items}>
                      <GroupContentStep
                        name={name}
                        groupColor={groupColor}
                        onNameChange={this.onNameChange}
                        unselectedComparisonItems={this.getUnselectedItems()}
                        selectedComparisonItems={this.getSelectedItems()}
                        onAddItem={this.onAddItem}
                        onRemoveItem={this.onRemoveItem}
                        isEditing
                      />
                    </Tabs.Content>
                    <Tabs.Content value={ComparisonModalGroupsTabOption.ValuesFees}>
                      <GroupValuesStep
                        name={name}
                        groupColor={groupColor}
                        onNameChange={this.onNameChange}
                        selectedItems={this.getSelectedItems()}
                        selectedItemDetails={groupProducts}
                        onFeeChange={this.onFeeChange}
                        onValueChange={this.onValueChange}
                        isEditing
                      />
                    </Tabs.Content>
                  </Stack>
                </Tabs>
              ) : (
                this.renderModalContent()
              )}
            </ModalBody>

            <ModalFooter>
              <Inline gap="200">
                {showPreviousButton && !groupToEdit && (
                  <Button variant="primary" onClick={this.onPrevious} icon="arrow-backward">
                    {formatMessage('AddComparisonGroupModal.Modal.Buttons.Previous')}
                  </Button>
                )}

                <Button variant="secondary" onClick={this.onCancel}>
                  {formatMessage('Common.Cancel')}
                </Button>

                {!!groupToEdit && (
                  <Button variant="primary" onClick={this.onAddGroup} type="submit" disabled={this.addGroupIsDisabled()}>
                    {formatMessage('AddComparisonGroupModal.Modal.Buttons.Save')}
                  </Button>
                )}

                {showNextButton && !groupToEdit && (
                  <Button variant="primary" type="submit" onClick={this.onNext} disabled={nextIsDisabled} icon="arrow-forward" iconPosition="right">
                    {formatMessage('AddComparisonGroupModal.Modal.Buttons.Next')}
                  </Button>
                )}

                {showAddGroupButton && !groupToEdit && (
                  <Button variant="primary" disabled={this.addGroupIsDisabled()} onClick={this.onAddGroup} type="submit">
                    {formatMessage('AddComparisonGroupModal.Modal.Buttons.AddGroup')}
                  </Button>
                )}
              </Inline>
            </ModalFooter>
          </form>
        </div>
      </Modal>
    );
  }
}

export default AddComparisonGroupModal;
